import React, { useState, useEffect, useMemo } from 'react';
import '../styles/AdminStock.css';

const AdminStock = ({ isOpen, onClose, onNuevo }) => {
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtro, setFiltro] = useState('');
  const [soloStockBajo, setSoloStockBajo] = useState(false);
  const [editando, setEditando] = useState(null);
  const [formEdit, setFormEdit] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState({ id: null, tipo: '', texto: '' });

  useEffect(() => {
    if (!isOpen) return;
    cargarProductos();
  }, [isOpen]);

  const cargarProductos = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/productos');
      const data = await res.json();
      setProductos(Array.isArray(data) ? data : []);
    } catch {
      setProductos([]);
    } finally {
      setLoading(false);
    }
  };

  const productosFiltrados = useMemo(() => {
    return productos.filter(p => {
      if (filtro && !p.nombre.toLowerCase().includes(filtro.toLowerCase())) return false;
      if (soloStockBajo && (p.stock ?? 0) > (p.stock_minimo ?? 3)) return false;
      return true;
    });
  }, [productos, filtro, soloStockBajo]);

  const stats = useMemo(() => {
    const conStockBajo = productos.filter(p => (p.stock ?? 0) <= (p.stock_minimo ?? 3)).length;
    const valorInventario = productos.reduce(
      (acc, p) => acc + ((p.costo ?? 0) * (p.stock ?? 0)), 0
    );
    const unidadesTotales = productos.reduce((acc, p) => acc + (p.stock ?? 0), 0);
    return { total: productos.length, conStockBajo, valorInventario, unidadesTotales };
  }, [productos]);

  const abrirEdicion = (producto) => {
    if (editando === producto._id) {
      setEditando(null);
      return;
    }
    setEditando(producto._id);
    setFormEdit({
      stock:        producto.stock        ?? 0,
      stock_minimo: producto.stock_minimo ?? 3,
      proveedor:    producto.proveedor    ?? '',
      costo:        producto.costo        ?? '',
      recargo:      producto.recargo      ?? '',
    });
    setMensaje({ id: null, tipo: '', texto: '' });
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormEdit(prev => ({ ...prev, [name]: value }));
  };

  const precioCalculado = () => {
    const costo   = parseFloat(formEdit.costo);
    const recargo = parseFloat(formEdit.recargo);
    if (!isNaN(costo) && !isNaN(recargo) && costo > 0 && recargo >= 0) {
      return Math.round(costo * (1 + recargo / 100));
    }
    return null;
  };

  const ajustarStock = async (producto, delta) => {
    const nuevoStock = Math.max(0, (producto.stock ?? 0) + delta);
    try {
      const res = await fetch(`/api/productos/${producto._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stock: nuevoStock }),
      });
      if (res.ok) {
        setProductos(prev =>
          prev.map(p => p._id === producto._id ? { ...p, stock: nuevoStock } : p)
        );
      }
    } catch { /* silencioso */ }
  };

  const guardarCambios = async (productoId) => {
    setGuardando(true);
    try {
      const payload = {
        stock:        Math.max(0, parseInt(formEdit.stock)        || 0),
        stock_minimo: Math.max(0, parseInt(formEdit.stock_minimo) || 0),
        proveedor:    formEdit.proveedor,
      };
      if (formEdit.costo   !== '') payload.costo   = parseFloat(formEdit.costo);
      if (formEdit.recargo !== '') payload.recargo = parseFloat(formEdit.recargo);

      const res = await fetch(`/api/productos/${productoId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();

      setProductos(prev =>
        prev.map(p => p._id === productoId ? { ...p, ...payload } : p)
      );
      setMensaje({ id: productoId, tipo: 'ok', texto: 'Guardado ✓' });
      setTimeout(() => { setEditando(null); setMensaje({ id: null, tipo: '', texto: '' }); }, 1400);
    } catch {
      setMensaje({ id: productoId, tipo: 'error', texto: 'Error al guardar' });
    } finally {
      setGuardando(false);
    }
  };

  const aplicarPrecioCalculado = async (productoId) => {
    const pc = precioCalculado();
    if (!pc) return;
    setGuardando(true);
    try {
      const payload = {
        stock:        Math.max(0, parseInt(formEdit.stock)        || 0),
        stock_minimo: Math.max(0, parseInt(formEdit.stock_minimo) || 0),
        proveedor:    formEdit.proveedor,
        costo:        parseFloat(formEdit.costo),
        recargo:      parseFloat(formEdit.recargo),
        precio:       pc,
      };
      const res = await fetch(`/api/productos/${productoId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();

      setProductos(prev =>
        prev.map(p => p._id === productoId ? { ...p, ...payload } : p)
      );
      setMensaje({ id: productoId, tipo: 'ok', texto: `Precio → $${pc.toLocaleString('es-AR')} ✓` });
      setTimeout(() => { setEditando(null); setMensaje({ id: null, tipo: '', texto: '' }); }, 1800);
    } catch {
      setMensaje({ id: productoId, tipo: 'error', texto: 'Error al aplicar precio' });
    } finally {
      setGuardando(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="stock-overlay" onClick={onClose}>
      <div className="stock-panel" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="stock-header">
          <h2>📊 Control de Stock</h2>
          <div className="stock-header-acciones">
            <button className="stock-btn-nuevo" onClick={onNuevo}>➕ Nuevo producto</button>
            <button className="stock-close" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* Estadísticas */}
        <div className="stock-stats">
          <div className="stock-stat">
            <span className="stat-numero">{stats.total}</span>
            <span className="stat-label">Artículos</span>
          </div>
          <div className="stock-stat">
            <span className="stat-numero">{stats.unidadesTotales}</span>
            <span className="stat-label">Unidades</span>
          </div>
          <div className={`stock-stat ${stats.conStockBajo > 0 ? 'stat-alerta' : ''}`}>
            <span className="stat-numero">{stats.conStockBajo}</span>
            <span className="stat-label">Stock Bajo ⚠️</span>
          </div>
          <div className="stock-stat">
            <span className="stat-numero">
              ${stats.valorInventario.toLocaleString('es-AR', { maximumFractionDigits: 0 })}
            </span>
            <span className="stat-label">Valor Inventario</span>
          </div>
        </div>

        {/* Filtros */}
        <div className="stock-filtros">
          <input
            type="text"
            placeholder="🔍 Buscar producto..."
            value={filtro}
            onChange={e => setFiltro(e.target.value)}
            className="stock-search"
          />
          <label className="stock-toggle-alerta">
            <input
              type="checkbox"
              checked={soloStockBajo}
              onChange={e => setSoloStockBajo(e.target.checked)}
            />
            Solo stock bajo
          </label>
        </div>

        {/* Tabla header */}
        <div className="stock-tabla-header">
          <span>Producto</span>
          <span className="col-stock">Stock</span>
          <span className="col-precio">Precio</span>
          <span className="col-editar"></span>
        </div>

        {/* Lista */}
        <div className="stock-lista">
          {loading && <p className="stock-estado">Cargando...</p>}
          {!loading && productosFiltrados.length === 0 && (
            <p className="stock-estado">No hay productos{soloStockBajo ? ' con stock bajo' : ''}.</p>
          )}

          {productosFiltrados.map(prod => {
            const stockActual = prod.stock ?? 0;
            const stockMin    = prod.stock_minimo ?? 3;
            const esBajo      = stockActual <= stockMin;
            const estaEditando = editando === prod._id;
            const msgActivo    = mensaje.id === prod._id;

            return (
              <div key={prod._id} className={`stock-fila ${esBajo ? 'stock-bajo' : ''} ${estaEditando ? 'expandida' : ''}`}>

                {/* Fila principal */}
                <div className="stock-fila-main">

                  {/* Miniatura */}
                  <div className="stock-thumb-wrap">
                    {(prod.imagenes?.[0] || prod.imagen) ? (
                      <img
                        src={prod.imagenes?.[0] || prod.imagen}
                        alt={prod.nombre}
                        className="stock-thumb"
                        onError={e => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="stock-thumb-placeholder">📦</div>
                    )}
                  </div>

                  {/* Info producto */}
                  <div className="stock-info">
                    <span className="stock-nombre">{prod.nombre}</span>
                    <div className="stock-meta">
                      {prod.categoria && <span className="stock-cat">{prod.categoria}</span>}
                      {prod.proveedor  && <span className="stock-proveedor">🏭 {prod.proveedor}</span>}
                    </div>
                  </div>

                  {/* Control de stock */}
                  <div className="stock-control">
                    <button
                      className="stock-adj"
                      onClick={() => ajustarStock(prod, -1)}
                      disabled={stockActual === 0}
                      title="Restar 1 unidad"
                    >−</button>
                    <span className={`stock-cantidad ${esBajo ? 'cantidad-baja' : ''}`}>
                      {stockActual}
                      {esBajo && <span className="badge-alerta">!</span>}
                    </span>
                    <button
                      className="stock-adj"
                      onClick={() => ajustarStock(prod, 1)}
                      title="Sumar 1 unidad"
                    >+</button>
                  </div>

                  {/* Precio */}
                  <div className="stock-precio-col">
                    <span className="stock-precio">${prod.precio?.toLocaleString('es-AR')}</span>
                    {prod.costo > 0 && prod.recargo > 0 && (
                      <span className="stock-margen">
                        +{prod.recargo}% recargo
                      </span>
                    )}
                  </div>

                  {/* Botón editar */}
                  <button
                    className={`stock-btn-editar ${estaEditando ? 'activo' : ''}`}
                    onClick={() => abrirEdicion(prod)}
                    title={estaEditando ? 'Cerrar' : 'Editar'}
                  >
                    {estaEditando ? '▲' : '✎'}
                  </button>
                </div>

                {/* Panel de edición expandible */}
                {estaEditando && (
                  <div className="stock-edicion">
                    <div className="stock-edicion-grid">

                      <div className="stock-campo">
                        <label>Stock actual</label>
                        <input
                          type="number" name="stock"
                          value={formEdit.stock}
                          onChange={handleFormChange}
                          min="0"
                        />
                      </div>

                      <div className="stock-campo">
                        <label>Stock mínimo (alerta)</label>
                        <input
                          type="number" name="stock_minimo"
                          value={formEdit.stock_minimo}
                          onChange={handleFormChange}
                          min="0"
                        />
                      </div>

                      <div className="stock-campo stock-campo-wide">
                        <label>Proveedor</label>
                        <input
                          type="text" name="proveedor"
                          value={formEdit.proveedor}
                          onChange={handleFormChange}
                          placeholder="Nombre, contacto o empresa"
                        />
                      </div>

                      <div className="stock-campo">
                        <label>Costo ($)</label>
                        <input
                          type="number" name="costo"
                          value={formEdit.costo}
                          onChange={handleFormChange}
                          placeholder="0.00" min="0" step="0.01"
                        />
                      </div>

                      <div className="stock-campo">
                        <label>Recargo (%)</label>
                        <input
                          type="number" name="recargo"
                          value={formEdit.recargo}
                          onChange={handleFormChange}
                          placeholder="30" min="0" step="0.1"
                        />
                      </div>

                      <div className="stock-campo stock-precio-calc">
                        <label>Precio calculado</label>
                        {precioCalculado() !== null ? (
                          <div className="precio-calc-resultado">
                            <span className="precio-calc-valor">
                              ${precioCalculado().toLocaleString('es-AR')}
                            </span>
                            <button
                              type="button"
                              className="btn-aplicar-precio"
                              onClick={() => aplicarPrecioCalculado(prod._id)}
                              disabled={guardando}
                              title="Reemplaza el precio del catálogo con este valor"
                            >
                              Aplicar al catálogo
                            </button>
                          </div>
                        ) : (
                          <span className="precio-calc-hint">Ingresá costo y recargo</span>
                        )}
                      </div>

                    </div>

                    {msgActivo && (
                      <div className={`stock-mensaje ${mensaje.tipo === 'ok' ? 'stock-ok' : 'stock-error'}`}>
                        {mensaje.texto}
                      </div>
                    )}

                    <div className="stock-edicion-botones">
                      <button
                        className="btn-cancelar-stock"
                        onClick={() => setEditando(null)}
                        disabled={guardando}
                      >
                        Cancelar
                      </button>
                      <button
                        className="btn-guardar-stock"
                        onClick={() => guardarCambios(prod._id)}
                        disabled={guardando}
                      >
                        {guardando ? 'Guardando...' : 'Guardar cambios'}
                      </button>
                    </div>
                  </div>
                )}

              </div>
            );
          })}
        </div>

      </div>
    </div>
  );
};

export default AdminStock;
